import { useEffect, useState } from 'react';
import axios from 'axios';

export interface OAuthProvider {
  name: string;
  display_name: string;
  icon?: string;
}

interface UseOAuthProvidersResult {
  providers: OAuthProvider[];
  authServerUrl: string;
  janusEnabled: boolean;
  loading: boolean;
}

/**
 * Fetches the configured OAuth providers and the auth server's own URL.
 * Shared by Login (which lists every provider) and Landing (which skips
 * straight to the single provider when there's only one configured).
 */
export const useOAuthProviders = (): UseOAuthProvidersResult => {
  const [providers, setProviders] = useState<OAuthProvider[]>([]);
  const [authServerUrl, setAuthServerUrl] = useState<string>('');
  const [janusEnabled, setJanusEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [configResponse, providersResponse] = await Promise.all([
          axios.get('/api/auth/config'),
          axios.get('/api/auth/providers')
        ]);
        if (cancelled) return;
        setAuthServerUrl(configResponse.data.auth_server_url || 'http://localhost:8888');
        setJanusEnabled(Boolean(configResponse.data.janus_enabled));
        setProviders(providersResponse.data.providers || []);
      } catch (error) {
        console.error('[useOAuthProviders] Failed to fetch OAuth config/providers:', error);
        if (!cancelled) {
          setAuthServerUrl('http://localhost:8888');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { providers, authServerUrl, janusEnabled, loading };
};
